import React, { useState, useCallback, useRef, useEffect } from 'react';

export interface TooltipRow {
  label: string;
  value: string | number;
  color?: string;
  /** If true, renders as a subtle secondary row */
  dim?: boolean;
}

export interface TooltipData {
  title: string;
  subtitle?: string;
  /** Optional label shown as a small header above the rows (e.g. "User ID") */
  rowsHeader?: string;
  rows?: TooltipRow[];
}

interface TooltipState {
  data: TooltipData;
  x: number;
  y: number;
}

interface ChartTooltipProps {
  tooltip: TooltipState | null;
}

const OFFSET = 14;
const MARGIN = 12; // min gap from viewport edge

export const ChartTooltip: React.FC<ChartTooltipProps> = ({ tooltip }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    if (!tooltip || !ref.current) { setFlipped(false); return; }
    const w = ref.current.offsetWidth || 260;
    const spaceRight = window.innerWidth - tooltip.x - OFFSET - MARGIN;
    setFlipped(spaceRight < w);
  }, [tooltip]);

  if (!tooltip) return null;
  const { data, x, y } = tooltip;

  const left = flipped ? x - OFFSET - (ref.current?.offsetWidth ?? 260) : x + OFFSET;

  return (
    <div
      ref={ref}
      className="ct-root"
      style={{ left, top: y + OFFSET / 2 }}
      onMouseEnter={e => e.stopPropagation()}
    >
      <div className="ct-title">{data.title}</div>
      {data.subtitle && <div className="ct-subtitle">{data.subtitle}</div>}
      {data.rows && data.rows.length > 0 && (
        <>
          <div className="ct-divider" />
          {data.rowsHeader && (
            <div className="ct-rows-header">{data.rowsHeader}</div>
          )}
          <div className="ct-rows">
            {data.rows.map((row, i) => (
              <div key={i} className={`ct-row${row.dim ? ' ct-row--dim' : ''}`}>
                <span className="ct-row-label">
                  {row.color && (
                    <span className="ct-swatch" style={{ background: row.color }} />
                  )}
                  {row.label}
                </span>
                <span className="ct-row-value">{row.value}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/** Hook that manages tooltip state and returns mouse handlers + tooltip JSX. */
export function useChartTooltip() {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((data: TooltipData, e: React.MouseEvent) => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setTooltip({ data, x: e.clientX, y: e.clientY });
  }, []);

  const move = useCallback((e: React.MouseEvent) => {
    setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : prev);
  }, []);

  const hide = useCallback(() => {
    hideTimer.current = setTimeout(() => setTooltip(null), 80);
  }, []);

  const tooltipEl = <ChartTooltip tooltip={tooltip} />;

  return { show, move, hide, tooltipEl };
}
